/**
 * Converts HTML from ReactQuill to WhatsApp-compatible text format
 * Preserves formatting using WhatsApp's markdown-like syntax
 */
export const convertHtmlToWhatsApp = (html?: string): string => {
  if (typeof html !== 'string') return '';
  
  let formatted = html;
  
  // First, handle lists before removing other tags
  // Convert ordered lists - add line break before list
  formatted = formatted.replace(/<ol[^>]*>(.*?)<\/ol>/gis, (match, content) => {
    let counter = 1;
    let listContent = content.replace(/<li[^>]*>(.*?)<\/li>/gis, (liMatch: string, liContent: string) => {
      // Process the li content to handle nested formatting
      let cleanContent = liContent
        .replace(/<strong[^>]*>(.*?)<\/strong>/gi, ' *$1* ')
        .replace(/<b[^>]*>(.*?)<\/b>/gi, ' *$1* ')
        .replace(/<em[^>]*>(.*?)<\/em>/gi, ' _$1_ ')
        .replace(/<i[^>]*>(.*?)<\/i>/gi, ' _$1_ ')
        .replace(/<u[^>]*>(.*?)<\/u>/gi, ' ~$1~ ')
        .replace(/<br\s*\/?>(\n)?/gi, ' ') // Convert br to space within list items
        .replace(/<[^>]*>/g, '') // Remove remaining HTML tags
        .replace(/\s+/g, ' ') // Normalize spaces
        .trim();
      return `\n${counter++}. ${cleanContent}`;
    });
    return '\n' + listContent + '\n'; // Add line break before AND after list
  });
  
  // Convert unordered lists - add line break before list
  formatted = formatted.replace(/<ul[^>]*>(.*?)<\/ul>/gis, (match, content) => {
    let listContent = content.replace(/<li[^>]*>(.*?)<\/li>/gis, (liMatch: string, liContent: string) => {
      // Process the li content to handle nested formatting
      let cleanContent = liContent
        .replace(/<strong[^>]*>(.*?)<\/strong>/gi, ' *$1* ')
        .replace(/<b[^>]*>(.*?)<\/b>/gi, ' *$1* ')
        .replace(/<em[^>]*>(.*?)<\/em>/gi, ' _$1_ ')
        .replace(/<i[^>]*>(.*?)<\/i>/gi, ' _$1_ ')
        .replace(/<u[^>]*>(.*?)<\/u>/gi, ' ~$1~ ')
        .replace(/<br\s*\/?>(\n)?/gi, ' ') // Convert br to space within list items
        .replace(/<[^>]*>/g, '') // Remove remaining HTML tags
        .replace(/\s+/g, ' ') // Normalize spaces
        .trim();
      return `\n• ${cleanContent}`;
    });
    return '\n' + listContent + '\n'; // Add line break before AND after list
  });
  
  // Handle empty paragraphs with just <br> - these should create line breaks
  formatted = formatted.replace(/<p[^>]*>\s*<br\s*\/?>\s*<\/p>/gi, '\n');
  
  // Now handle other formatting that wasn't inside lists
  // Add spaces around formatting to ensure WhatsApp recognizes them
  formatted = formatted.replace(/<strong[^>]*>\s*(.*?)\s*<\/strong>/gi, (match, content) => {
    return ` *${content.trim()}* `;
  });
  formatted = formatted.replace(/<b[^>]*>\s*(.*?)\s*<\/b>/gi, (match, content) => {
    return ` *${content.trim()}* `;
  });
  
  // Convert italic tags to WhatsApp format
  formatted = formatted.replace(/<em[^>]*>\s*(.*?)\s*<\/em>/gi, (match, content) => {
    return ` _${content.trim()}_ `;
  });
  formatted = formatted.replace(/<i[^>]*>\s*(.*?)\s*<\/i>/gi, (match, content) => {
    return ` _${content.trim()}_ `;
  });
  
  // Convert underline tags to WhatsApp format (using tilde for strikethrough)
  formatted = formatted.replace(/<u[^>]*>\s*(.*?)\s*<\/u>/gi, (match, content) => {
    return ` ~${content.trim()}~ `;
  });
  
  // Convert line breaks within paragraphs
  formatted = formatted.replace(/<br\s*\/?>(\n)?/gi, '\n');
  
  // Convert paragraphs to line breaks - handle differently for manual lists
  formatted = formatted.replace(/<\/p>\s*<p[^>]*>/gi, '\n\n'); // Between paragraphs - double line break
  formatted = formatted.replace(/<\/p>/gi, '\n'); // End of last paragraph
  formatted = formatted.replace(/<p[^>]*>/gi, ''); // Remove opening p tags
  
  // Remove any remaining HTML tags
  formatted = formatted.replace(/<[^>]*>/g, '');
  
  // Clean up entities
  formatted = formatted
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");
  
  // Clean up formatting while preserving structure
  formatted = formatted
    .replace(/[ \t]+/g, ' ') // Normalize multiple spaces to single space
    .replace(/^[ \t]+|[ \t]+$/gm, '') // Remove leading/trailing spaces from each line
  
  // Post-process to fix manual list formatting
  const lines = formatted.split('\n');
  const result = [];
  
  for (let i = 0; i < lines.length; i++) {
    const currentLine = lines[i];
    
    result.push(currentLine);
    
    // Look ahead to see if we need to adjust spacing
    let j = i + 1;
    
    // If current line has content and we find a list item after skipping empty lines
    if (currentLine.trim()) {
      // Look for the next non-empty line
      while (j < lines.length && lines[j].trim() === '') {
        j++;
      }
      
      // If the next non-empty line is a list item starting with dash
      if (j < lines.length && lines[j].trim().startsWith('- ')) {
        // We want only single line break before the list
        // Skip all empty lines between current and the list item
        i = j - 1; // Will be incremented by the loop, so we'll be at j next iteration
        continue;
      }
    }
    
    // If current line is a list item
    if (currentLine.trim().startsWith('- ')) {
      // Look for the next non-empty line
      while (j < lines.length && lines[j].trim() === '') {
        j++;
      }
      
      // If the next non-empty line is also a list item
      if (j < lines.length && lines[j].trim().startsWith('- ')) {
        // Skip empty lines between list items
        i = j - 1; // Will be incremented by the loop
        continue;
      }
    }
  }
  
  formatted = result.join('\n')
    .replace(/\n{3,}/g, '\n\n') // Limit to maximum double line breaks
    .trim();
  
  return formatted;
};

/**
 * Processes text by converting HTML to WhatsApp format and replacing variables with examples
 */
export const processTextWithVariables = (
  text: string, 
  variables: Array<{ position: number; example: string; component_type: string }>,
  componentType: string
): string => {
  // First convert HTML to WhatsApp format
  let processed = convertHtmlToWhatsApp(text);
  
  // Then replace variables with examples
  const componentVariables = variables.filter(v => v.component_type === componentType);
  componentVariables.forEach((variable) => {
    const regex = new RegExp(`\\{\\{${variable.position}\\}\\}`, 'g');
    processed = processed.replace(regex, variable.example);
  });
  
  return processed;
};
